import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { documentosApi } from '@/services/api'
import type { DocumentoGenerado } from '@/types'
import {
  cuerpoAlbaran,
  formularioInicial,
  lineaVacia,
  type AlbaranForm,
  type AlbaranLineaForm,
  type ContextoAlbaran,
} from '@/utils/albaranRecogida'
import { mensajeDeError } from '@/components/pedidos-ventas/documentos/piezas'

/** Estado del albarán de recogida: precarga, formulario y generación.
 *
 *  Vive en el cajón y no en el panel por la misma razón que el formulario del
 *  packing: volver del PDF al formulario, o cambiar de documento y regresar, no
 *  puede perder lo tecleado. Y **en un rechazo tampoco se pierde**: la mutación
 *  falla, el mensaje se queda en el pie y el formulario sigue donde estaba.
 *
 *  La consulta tarda ~3,6 s en frío, así que se lanza en cuanto el panel se
 *  abre (`activo`) y no al pulsar «Generar»: la espera se solapa con lo que la
 *  persona teclea. */
export function useAlbaranRecogida({
  empresa, carga, contexto, activo, onGenerado,
}: {
  empresa: string
  carga: string
  contexto: ContextoAlbaran
  /** El panel del albarán está a la vista. */
  activo: boolean
  onGenerado: (pdf: DocumentoGenerado) => void
}) {
  const { t, i18n } = useTranslation()
  const idioma: 'es' | 'en' = i18n.language?.startsWith('en') ? 'en' : 'es'

  const consulta = useQuery({
    queryKey: ['albaran-recogida-datos', empresa, carga],
    queryFn: () => documentosApi.getAlbaranRecogidaDatos(empresa, carga),
    enabled: activo,
    staleTime: 2 * 60 * 1000,
    retry: 1,
    // Un refetch al volver el foco reemplazaría los datos justo cuando se elige
    // carpeta de descarga.
    refetchOnWindowFocus: false,
  })
  const datos = consulta.data?.data

  const [form, setForm] = useState<AlbaranForm | null>(null)

  // El formulario arranca UNA vez, con lo que llegue. Si la consulta falla se
  // arranca igualmente con lo que la carga ya sabe: el documento se puede
  // rellenar a mano, y bloquearlo por un fallo de precarga sería peor.
  useEffect(() => {
    if (form) return
    if (datos || consulta.isError) setForm(formularioInicial(datos, contexto))
  }, [form, datos, consulta.isError, contexto])

  const generar = useMutation({
    mutationFn: () => {
      if (!form) throw new Error('formulario sin iniciar')
      return documentosApi.generarAlbaranRecogida(cuerpoAlbaran({ empresa, carga, idioma, form }))
    },
    onSuccess: (r) => onGenerado(r.data),
  })

  const errorGen = generar.isError
    ? (mensajeDeError(generar.error) ?? t('salesOrders.documentos.errorGenerar'))
    : null

  const poner = (parcial: Partial<Omit<AlbaranForm, 'lineas'>>) =>
    setForm(f => (f ? { ...f, ...parcial } : f))
  const ponerLinea = (id: number, parcial: Partial<Omit<AlbaranLineaForm, 'id'>>) =>
    setForm(f => (f ? { ...f, lineas: f.lineas.map(l => (l.id === id ? { ...l, ...parcial } : l)) } : f))
  const anadirLinea = () =>
    setForm(f => (f ? { ...f, lineas: [...f.lineas, lineaVacia()] } : f))
  const quitarLinea = (id: number) =>
    setForm(f => (f ? { ...f, lineas: f.lineas.filter(l => l.id !== id) } : f))

  return {
    datos,
    cargando: activo && !form && consulta.isLoading,
    errorDatos: consulta.isError,
    form,
    poner, ponerLinea, anadirLinea, quitarLinea,
    generar,
    errorGen,
  }
}

export type AlbaranRecogidaEstado = ReturnType<typeof useAlbaranRecogida>
